<template>
  <div
    :key="key"
    :class="[{
      'inline-field': isExpandedMode,
      'table-cell-wrapper__selected': isSelectedCell && allowShowSelectCol,
      'table-cell-wrapper__selected--one': isOneCellSelected,
      'table-cell-wrapper__attachment': isAttachmentCell,
    }]"
    class="table-cell-wrapper fs-16 gap-2"
    @keydown="editActions"
    @click.exact="onExactClick"
    @click.ctrl.exact="setMultipleCtrlCells"
    @click.shift.exact="setMultipleShiftCells"
    @click.meta.exact="setMultipleCtrlCells"
    @mousedown="onSetKeyboardControllerDisabled"
    @dblclick="doubleClickCell">
    <div
      :class="{
        [`table-cell-buttons table-cell-buttons__${getSelectedRowHeight}`]: !isOpenedInDialog,
        'table-cell-buttons-editing': editMode,
      }"
      class="d-flex align-center justify-space-between">
      <div
        v-if="!hideHeader"
        class="fs-12 mr-2 font-weight-bold font-fam-poppins darkGrey--text text-uppercase">
        {{ header.text }}
        <span
          v-if="isRequired"
          class="red--text">*</span>
      </div>
      <v-spacer v-else />

      <div
        :class="{ 'table-cell-buttons-inner py-2': !isOpenedInDialog }"
        class="d-flex align-center px-3 gap-1 rounded-pill">
        <slot
          name="cellActions"
          :doubleClickCell="doubleClickCell" />

        <slot
          v-if="!hideActions"
          name="additionalCellActions"
          :borderedCell="borderedCell"
          :isCellForPasting="isPasteCell" />
      </div>

      <div
        v-if="!isExpandedMode"
        class="py-2">
        <slot name="cellNotification" />
      </div>
    </div>
    <div
      class="table-cell-wrapper__selected-wrapper"
      :class="{
        'table-cell-wrapper__selected--one-overlay': isSelectedCell && allowShowSelectCol && isAllowedToSelectOne,
        'table-cell-wrapper__selected--edited-cell' : isOverlayedEditCell
      }"
      style="height: 100%">
      <!-- Note we don't want this div to be position relativen normally!
           - Breaks the cell editing in tables.
           - Honestly this design sucks ass and the logic/view should be split from
             expandedView + tableView, should have been refactored in V2. -->
      <div
        v-if="component"
        class="component-wrapper"
        :class="{ 'position-relative': isLoading && isExpandedMode }">
        <component
          :is="component"
          :ref="getRefOfCell"
          :bordered-cell="borderedCell"
          :cell-customizations="cellCustomizations"
          :col="columnName"
          :collection-id="collectionId"
          :current-row-id="currentRowId"
          :disabled-edit-mode="disabledEditMode"
          :edit-mode="editMode"
          :expanded-row="expandedRow"
          :has-access-editor="hasAccessToField"
          :header="header"
          :hidden-row="hiddenRow"
          :hint="header.column.hint"
          :is-array="isHeaderTypeArray"
          :is-expanded-mode="isExpandedMode"
          :is-dialog-collection="isDialogCollection"
          :is-opened-in-dialog="isOpenedInDialog"
          :is-overlayed="isOneCellSelected"
          :item="item"
          :mapped-dropdowns-of-view="mappedDropdownsOfView"
          :open-link-in-new-tab="openLinkInNewTab"
          :select-items="header.items ? header.items : []"
          :should-expand-height="shouldExpandHeight"
          :show-pensil="showPensil"
          :style-filled-img="styleFilledImg"
          :table-id="scheduleId"
          :transform-images-to-data="transformImagesToData"
          :value="value"
          :view="view"
          v-on="$listeners"
          @messagesNoHasAccessEditor="messagesNoHasAccessEditor"
          @dblclickHandler="doubleClickCell"
          @toggleEditMode="toggleEditMode"
          @setEditMode="setEditMode"
          @change="updateCell($event, columnName, currentRowId)"
          @saveFileProcess="onUpdateImages" />
        <div
          v-if="isLoading"
          class="loading-overlay">
          <img
            src="@/assets/icons/circle-loader.gif"
            class="loading-overlay-spinner">
        </div>
      </div>
      <div v-else>
        {{ Array.isArray(value) ? value.join(', ') : value }}
      </div>
    </div>
  </div>
</template>
<script>
import ProjectViewsApi from '@/services/graphql/projectViews';
import {
  mapMutations, mapState, mapActions, mapGetters,
} from 'vuex';
import { hasEditorAccess } from '@/utils';
import {
  NOT_PERMITTED_ACCESS, TYPE_EDITOR,
} from '@/constants/userPermissions';
import { LOCKED_FIELD } from '@/constants/notificationMessages';
import { KEYBOARD_KEYS } from '@/constants/keyboardController';
import productCellsTypes from '@/constants/productCellsTypes';
export default {
  name: 'ProductCell',
  props: {
    allowSaveOnEsc: {
      type: Boolean,
      default: false,
    },
    allowSetMultupleCells: {
      type: Boolean,
      default: false,
    },
    allowShowSelectCol: {
      type: Boolean,
      default: false,
    },
    borderedCell: {
      type: Boolean,
      default: false,
    },
    cellCustomizations: {
      type: Object,
      default: () => {},
    },
    collectionId: {
      type: String,
      default: '',
    },
    data: {
      type: Array,
      default: () => [],
    },
    disabledEditMode: {
      type: Boolean,
      default: () => false,
    },
    expandedRow: {
      type: Boolean,
      default: false,
    },
    header: {
      type: Object,
      required: true,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    isDialogCollection: {
      type: Boolean,
      default: true,
    },
    isExpandedMode: {
      type: Boolean,
      default: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isOpenedInDialog: {
      type: Boolean,
      default: true,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
    mappedDropdownsOfView: {
      type: Object,
      default: () => {
      },
    },
    openLinkInNewTab: {
      type: Boolean,
      default: false,
    },
    searchMode: {
      type: Boolean,
      default: false,
    },
    shouldExpandHeight: {
      type: Boolean,
      default: false,
    },
    transformImagesToData: {
      type: Boolean,
      default: false,
    },
    view: {
      type: String,
      default: '',
      validator: (value) => {
        // productCreate comes from collections
        // projects-product-expanded comes from projects
        return ['projects-product-expanded', 'productCreate', ''].includes(value);
      },
    },
  },
  data() {
    return {
      editMode: this.expandedRow && !this.disabledEditMode,
      key: 0,
      escapePressed: false,
      defaultFieldsCollection: [],
    };
  },
  computed: {
    ...mapState('ProjectDetailsTableSchedule', [
      'scheduleId',
    ]),
    ...mapState(['role']),
    ...mapGetters({
      navigationWithKeyboardInSchedule: 'FeatureFlags/navigationWithKeyboardInSchedule',
      mappingControlFields: 'ProjectDetailsTableSchedule/mappingControlFields',
      isReadOnlyCell: 'ProjectDetailsTableSchedule/isReadOnlyCell',
      isReadOnlyModalVariant: 'Collections/isReadOnlyModalVariant',
      isUnsuccessfulPayment: 'UserProfile/isUnsuccessfulPayment',
      userId: 'userId',
      copiedContent: 'ProjectDetailsTableSchedule/copiedContent',
      canModifyProjectDocuments: 'UserRoles/canModifyProjectDocuments',
      isVersionMode: 'ProjectVersions/isVersionMode',
      findSelectedCell: 'ProjectDetailsTableSchedule/isSelectedCell',
      selectedCellsLength: 'ProjectDetailsTableSchedule/selectedCellsLength',
      getSelectedRowHeight: 'ScheduleViews/getSelectedRowHeight',
    }),
    allowedForAppropriateRoles() {
      return hasEditorAccess(this.role) && !!this.userId;
    },
    allowSpinnerOnUpdate() {
      return this.isAttachmentCell;
    },
    associatedDocumentsType() {
      return this.columnType === 'ASSOCIATED_DOCUMENTS';
    },
    canModifyProjectDocumentsCell() {
      return this.associatedDocumentsType && this.canModifyProjectDocuments;
    },
    columnName() {
      return this.header.value;
    },
    columnType() {
      return this.header.column.type;
    },
    component() {
      const { columnType: type } = this;
      return productCellsTypes[type] || null;
    },
    currentRowIndex() {
      const { mappedDataIds, currentRowId } = this;
      return mappedDataIds.findIndex(id => id === currentRowId);
    },
    currentRowId() {
      return this.item?.id;
    },
    getRefOfCell() {
      return `cell-${this.scheduleId}`;
    },
    hasAccessEditor() {
      if (!this.header.permissions?.length) return true;
      return this.header.permissions.find(e => e?.type === TYPE_EDITOR && this.isRoleEditor)?.edit ?? true;
    },
    hasAccessToField() {
      return !this.hasAccessEditor ? this.hasAccessEditor : !this.noHasAccessEditorToProductReference;
    },
    hideActions() {
      return this.associatedDocumentsType;
    },
    hiddenRow() {
      const { item, role } = this;
      return !hasEditorAccess(role) && item.status === 'hidden';
    },
    hiddenTypes() {
      return ['CHECKBOX', 'ORDER_COL', 'ORDER_COL'].includes(this.columnType);
    },
    isAllowedToSelectOne() {
      return [
        'DROPDOWN',
        'COMBOBOX',
        'ARRAY_STRING',
        'MULTILINE',
        'SINGLE',
        'FORMULA_STRING',
        'FIELD_PRODUCT_REFERENCE',
        'FIELD_PRODUCT_EQUAL',
        'NUMBER',
        'URL',
        'URL_MULTILINE',
        'ASSOCIATED_DOCUMENTS',
      ].includes(this.columnType);
    },
    isAttachmentCell() {
      return this.columnType === 'ARRAY_AIRTABLE_IMAGE';
    },
    isDefaultSaveCell() {
      const { columnType: type } = this;
      return ['ARRAY_STRING', 'MULTILINE', 'URL_MULTILINE', 'DROPDOWN', 'COMBOBOX'].includes(type);
    },
    isOneCellSelected() {
      return !!this.selectedCellsLength && this.borderedCell && this.isAllowedToSelectOne;
    },
    isOverlayedEditCell() {
      const { editMode, associatedDocumentsType: docs, isOpenedInDialog } = this;
      if (docs || isOpenedInDialog) return false;
      return editMode;
    },
    isRoleEditor() {
      return this.role === TYPE_EDITOR;
    },
    isHeaderTypeArray() {
      return this.columnType.includes('ARRAY');
    },
    isHeaderTypeFormula() {
      const { columnType, columnName, isReadOnlyCell, header } = this;
      return columnType.includes('FORMULA') || isReadOnlyCell(columnName) || isReadOnlyCell(header.id);
    },
    isNumberOrCurrencyField() {
      return ['NUMBER', 'CURRENCY'].includes(this.columnType);
    },
    isPasteCell() {
      const { columnName, copiedContent } = this;
      if (!Array.isArray(copiedContent)) return false;
      return copiedContent.some(e => e?.col === columnName);
    },
    isSelectedCell() {
      const { columnName, currentRowId } = this;
      return this.findSelectedCell({
        columnName,
        currentRowId,
      });
    },
    mappedDataIds() {
      return this.data.map(item => item.id);
    },
    noHasAccessEditorToProductReference() {
      const fields = this.mappingControlFields(this.defaultFieldsCollection);
      return fields.some(e => e.permissions?.some(e => e?.type === TYPE_EDITOR && this.isRoleEditor && e?.edit === false)) || false;
    },
    showPensil() {
      const {
        isUnsuccessfulPayment,
        hiddenTypes,
        allowedForAppropriateRoles,
        isHeaderTypeFormula,
        isVersionMode,
        isReadOnlyModalVariant,
        $route,
      } = this;
      if (isUnsuccessfulPayment) {
        return false;
      } else if (this.view === 'productCreate' || $route.name == 'collection-library') {
        return !hiddenTypes && !isReadOnlyModalVariant && !isHeaderTypeFormula;
      }
      return !hiddenTypes && allowedForAppropriateRoles && !isHeaderTypeFormula && !isVersionMode;
    },
    styleFilledImg() {
      return ['Image', 'Line Drawing'].includes(this.columnName) ? 'style1' : 'style2';
    },
    value() {
      if (this.header.value == 'Choose Collection' || this.header.type == 'SELECT') {
        return this.header.selectValue;
      }
      let value = this.item[this.columnName];
      if (this.isAttachmentCell) {
        if (!Array.isArray(value)) {
          value = [];
        }
      }
      return this.formatValue(value);
    },
  },
  watch: {
    disabledEditMode: {
      handler() {
        this.setInitEditMode();
      },
    },
    expandedRow: {
      handler() {
        this.setInitEditMode();
      },
    },
    editMode(val) {
      this.setEditCellEditMode(val);
    },
    showPensil: {
      handler(val) {
        if (!val && this.expandedRow && !this.searchMode) {
          this.editMode = false;
        }
      },
      immediate: true,
    },
  },
  created() {
    if (this.columnType === 'FIELD_PRODUCT_REFERENCE' && this.isRoleEditor) {
      this.getDefaultFieldsProductReference();
    }
  },
  methods: {
    ...mapMutations({
      spinner: 'spinner',
      openSnackBar: 'openSnackBar',
      changeUpdateProductMode: 'Collections/changeUpdateProductMode',
    }),
    ...mapActions({
      handleError: 'handleError',
      setCopiedContent: 'ProjectDetailsTableSchedule/setCopiedContent',
      setUndoActions: 'UndoActions/setUndoActions',
      setSelectedCells: 'ProjectDetailsTableSchedule/setSelectedCells',
      setEditCellEditMode: 'ProjectDetailsTableSchedule/setEditCellEditMode',
      setDisableKeyboardController: 'ProjectDetailsTableSchedule/setDisableKeyboardController',
    }),
    onUpdateImages(event = false) {
      this.$emit('saveFileProcess', event );
    },
    setInitEditMode() {
      this.editMode = this.expandedRow && !this.disabledEditMode;
    },
    async getDefaultFieldsProductReference() {
      try {
        this.spinner(true);
        const { data } = await ProjectViewsApi.getDefaultFieldsMapping({
          viewToSortBy: 'collection',
        });
        this.defaultFieldsCollection = data.response?.fieldsMapping.map(e => e?.collectionFieldName).filter(e => e) || [];
      } catch (err) {
        this.handleError(err);
      } finally {
        this.spinner(false);
      }
    },
    messagesNoHasAccessEditor() {
      const { noHasAccessEditorToProductReference, hasAccessEditor } = this;
      const title = !hasAccessEditor ? LOCKED_FIELD : '';
      const text = noHasAccessEditorToProductReference && hasAccessEditor ? NOT_PERMITTED_ACCESS : '';
      this.openSnackBar({
        title,
        text,
      });
    },
    doubleClickCell() {
      const { editMode, showPensil, setEditMode, hasAccessToField } = this;
      if (!hasAccessToField) {
        this.messagesNoHasAccessEditor();
        return false;
      }
      if (!editMode && showPensil) {
        setEditMode(true);
      }
    },
    formatValue(value) {
      if (this.isNumberOrCurrencyField) {
        return value ? value.toString() : '0';
      }
      return value === '\b' || value === ' ' ? '' : value;
    },
    editActions(event) {
      const { navigationWithKeyboardInSchedule: flag, allowSaveOnEsc } = this;
      const isEscapeKey = event.key === 'Escape';
      if (isEscapeKey && allowSaveOnEsc && flag) return false;
      else if (isEscapeKey) {
        this.toggleEditMode();
        this.escapePressed = true;
        this.key += 1;
      }
    },
    onSetKeyboardControllerDisabled() {
      this.setDisableKeyboardController(false);
    },
    onExactClick() {
      if (!this.isOpenedInDialog) {
        this.setSelectedCellAction();
        return;
      }
      if (!this.editMode && this.showPensil) {
        if (this.columnType === 'ARRAY_AIRTABLE_IMAGE' && Array.isArray(this.value) && this.value.length > 0) {
          return;
        } else {
          this.setEditMode(true);
        }
      }
    },
    setSelectedCellAction() {
      const {
        currentRowIndex,
        currentRowId: rowId,
        columnName: col,
        allowShowSelectCol,
        value,
      } = this;
      if (!allowShowSelectCol) return;
      const index = currentRowIndex;
      this.setSelectedCells([{
        rowId,
        col,
        index,
        value,
      }]);
    },
    setMultipleCtrlCells() {
      const {
        currentRowIndex,
        currentRowId,
        columnName,
        value,
        allowSetMultupleCells,
      } = this;
      if (!allowSetMultupleCells) return;
      this.$emit('setMultipleCtrlCells', {
        currentRowIndex,
        currentRowId,
        columnName,
        value,
      });
    },
    setMultipleShiftCells() {
      const {
        mappedDataIds,
        columnName,
        allowSetMultupleCells,
      } = this;
      if (!allowSetMultupleCells) return;
      this.setMultipleCtrlCells();
      this.$emit('setMultipleShiftCells', {
        mappedDataIds, columnName,
      });
    },
    async updateCell(ev, col, rowId, isPasteAction = false) {
      const {
        $route: route,
        view,
        allowSpinnerOnUpdate,
        columnName,
      } = this;
      if (this.escapePressed) {
        this.escapePressed = false;
        return;
      }
      this.escapePressed = false;
      if (view === 'productCreate' || route.name == 'collection-library') {
        const mode = this.item?.mode;
        this.$emit('updateProductField', {
          ev, col, rowId, mode, allowSpinnerOnUpdate, colHeader: this.header,
        });
        if (!allowSpinnerOnUpdate) {
          this.$set(this.item, columnName, ev.value ? ev.value : ev.values ? ev.values : null);
        }
      } else {
        this.$emit('updateCell', {
          ev,
          col,
          rowId,
          isPasteAction,
          columnName,
          columnType: this.columnType,
          oldVal: this.value,
        });
      }
    },
    toggleEditMode() {
      if (!this.expandedRow) {
        this.editMode = !this.editMode;
      }
    },
    setEditMode(val) {
      const {
        isVersionMode,
        disabledEditMode,
        isUnsuccessfulPayment,
        canModifyProjectDocumentsCell,
      } = this;
      if (isVersionMode || (disabledEditMode && !canModifyProjectDocumentsCell?.allowed) || isUnsuccessfulPayment) {
        return;
      }
      if (!this.expandedRow) {
        this.editMode = val;
      }
    },
    //NOTE: Method for Table Controller
    onUpdateAndClose(el, key = null) {
      const isTab = key === KEYBOARD_KEYS.TAB;
      const isEsc = key === KEYBOARD_KEYS.ESC;
      const { isDefaultSaveCell } = this;
      const inputCell = el.querySelector('textarea') || el.querySelector('input');
      if (isDefaultSaveCell && !isTab && !isEsc) return false;
      else if (inputCell) inputCell.blur();
    },
  },
};
</script>
<style scoped lang="scss">
@import 'src/assets/styles/__variables.scss';

td {
  .table-cell-wrapper {
    &__attachment {
      height: inherit;

      .component-wrapper {
        height: inherit;
      }

      ::v-deep .attachment-documents-wrapper {
        max-height: 164px;
        height: 94%;
        //overflow: hidden;
        display: flex;
        align-self: center;
      }
    }
  }
}

.table-cell-buttons {
  position: absolute;
  z-index: 1;

  &__Tall {
    top: $schedulePaddingTall;
    right: $schedulePaddingTall;
  }
  &__Medium {
    top: $schedulePaddingMedium;
    right: $schedulePaddingMedium;
  }
  &__Short {
    top: 3px;
    right: 3px;
  }

  .table-cell-buttons-inner {
    display: none !important;
    background: var(--v-lightGrey-base);
    padding: 5px 10px !important;
  }
}
.table-cell-buttons-editing {
  z-index: 11;
}

.table-cell-wrapper {
  position: relative;
  min-height: 21px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  align-self: stretch;

  &_no-height {
    height: auto;
  }

  &__selected {
    border: 1px solid var(--v-green-base);

    &--edited-cell {
      z-index: 10 !important;
    }

    .table-cell-buttons-inner {
      display: flex !important;
    }
  }

  &__pasting-cells {
    background: #eef4f5;
  }

  .edit-mode-activator {
    cursor: pointer;
    position: absolute;
    top: 0;
    left: -16px;
    display: none;
  }

  &:hover {
    .edit-mode-activator {
      display: block;
    }

    .table-cell-buttons-inner {
      display: flex !important;
    }
  }
}

.component-wrapper {
  width: 100%;

  ::v-deep.v-input {
    input, textarea {
      font-size: 16px !important;
    }

    input::placeholder, textarea::placeholder {
      font-size: 10px !important;
      opacity: 0.7;
      line-height: 13px;
    }
  }

  ::v-deep.v-chip__content {
    font-size: 16px;
  }

  .loading-overlay {
    display: flex;
    justify-content: center;
    align-items: center;

    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;

    background-color: var(--v-lightGrey-lighten1);
    opacity: 0.25;
  }

  .loading-overlay-spinner {
    // purposely set larger than box, because the spinner gif has some empty space
    max-height: 130%;
  }
}
.position-relative {
  position: relative;
}
</style>
